import React from 'react'
import { isMobile } from 'react-device-detect'
import { ProductImage } from 'app/common/Common'
import {
  PixelTable,
  PixelDate,
  PixelCurrency,
  PixelText,
  DcDiv,
} from 'pixel-base'
import styled from 'styled-components'

// Utility function to clean malformed date strings
const cleanDateString = (dateString: string): string => {
  if (!dateString) return ''

  // Handle double timezone issue: "2025-08-28T13:52:27+05:00+05:00" -> "2025-08-28T13:52:27+05:00"
  const cleanedDate = dateString.replace(/(\+\d{2}:\d{2})\+\d{2}:\d{2}$/, '$1')

  // Validate the date
  const date = new Date(cleanedDate)
  return isNaN(date.getTime()) ? '' : cleanedDate
}

interface StoreLink {
  store_id: number
  store_name: string
}

interface ProductVariation {
  product_id: number | null
  product_image_path: string | null
  product_name: string | null
  product_sku: string | null
  quantity_available: number | null
  product_price: number | null
  sale_price: number | null
  created_at: string | null
  permalink: string | null
  product_status: string | null
  store_links: StoreLink[]
}

interface Product {
  product_id: number
  product_image_path: string
  product_name: string
  product_sku: string
  quantity_available: number | null
  product_price: number
  sale_price: number
  created_at: string
  permalink: string
  product_status: string
  store_links: StoreLink[]
  variations: ProductVariation[]
}

interface ProductTableProps {
  products: Product[]
  loading: boolean
  totalRows: number
  pageSize: number
  currentPage: number
  onPageChange: (page: number) => void
  onPerRowsChange: (perPage: number) => void
  onRowClick: (row: Product) => void
  resetPaginationToggle: boolean
}

const ProductTable: React.FC<ProductTableProps> = ({
  products,
  loading,
  totalRows,
  pageSize,
  currentPage,
  onPageChange,
  onPerRowsChange,
  onRowClick,
  resetPaginationToggle,
}) => {
  const columns = [
    {
      dataKey: 'product_image_path',
      name: 'Image',
      width: '6%',
      selector: (row: Product) => (
        <ProductImage
          style={{
            width: '32px',
            marginRight: '5px',
          }}
          path={row.product_image_path}
        />
      ),
    },
    {
      dataKey: 'product_sku',
      name: 'SKU',
      width: '12%',
      sortable: true,
      selector: (row: Product) => (
        <DcDiv padding="10px">
          <PixelText copyToClipboard multiLine>
            {row.product_sku || '--'}
          </PixelText>
        </DcDiv>
      ),
    },
    {
      dataKey: 'product_name',
      name: 'Product Name',
      width: '20%',
      sortable: true,
      selector: (row: Product) => {
        const variationCount =
          row.variations?.filter((v) => v.product_id).length || 0
        return (
          <DcDiv padding="10px">
            <PixelText copyToClipboard multiLine>
              {row.product_name || '--'}
              {variationCount > 0 && (
                <VariationCount>
                  {variationCount} variation{variationCount !== 1 ? 's' : ''}
                </VariationCount>
              )}
            </PixelText>
          </DcDiv>
        )
      },
    },
    {
      dataKey: 'product_price',
      name: 'Price',
      width: '10%',
      sortable: true,
      selector: (row: Product) =>
        row.sale_price && row.sale_price !== row.product_price ? (
          <ProductPrice>
            <s>
              <PixelCurrency value={row.product_price} />
            </s>
            <PixelCurrency value={row.sale_price} />
          </ProductPrice>
        ) : (
          <PixelCurrency value={row.sale_price} />
        ),
    },
    {
      dataKey: 'quantity_available',
      name: 'Qty.',
      width: '6%',
      sortable: true,
      selector: (row: Product) => row.quantity_available || '--',
    },
    {
      dataKey: 'store_links',
      name: 'Stores',
      width: '15%',
      sortable: false,
      selector: (row: Product) => (
        <StoreChipsContainer>
          {row.store_links && row.store_links.length > 0 ? (
            row.store_links.map((store, index) => (
              <StoreChip key={`${store.store_id}-${index}`}>
                {store.store_name}
              </StoreChip>
            ))
          ) : (
            <span>--</span>
          )}
        </StoreChipsContainer>
      ),
    },
    {
      dataKey: 'product_status',
      name: 'Status',
      width: '7%',
      sortable: true,
      selector: (row: Product) => (
        <StatusBadge $isActive={row.product_status === 'active'}>
          {row.product_status}
        </StatusBadge>
      ),
    },
    {
      dataKey: 'created_at',
      name: 'Created At',
      width: '10%',
      sortable: true,
      selector: (row: Product) => {
        const cleanedDate = cleanDateString(row.created_at)
        return cleanedDate ? (
          <PixelDate
            className="date"
            value={cleanedDate}
            format="DD/MM/YYYY"
            showFullDatePopover={true}
          />
        ) : (
          '--'
        )
      },
    },
  ]

  return (
    <PixelTable
      columns={columns}
      data={products || []}
      progressPending={loading}
      paginationTotalRows={totalRows}
      fixedHeader
      fixedHeaderScrollHeight={
        isMobile ? 'calc(100vh - 230px)' : 'calc(100vh - 219px)'
      }
      paginationServer
      paginationPerPage={pageSize}
      paginationRowsPerPageOptions={[50, 100, 250]}
      onChangeRowsPerPage={onPerRowsChange}
      pointerOnHover={true}
      onRowClicked={onRowClick}
      onChangePage={onPageChange}
      pagination
    />
  )
}

const ProductPrice = styled.div`
  > s {
    color: red;
    display: block;
    font-size: 10px;
  }
`

const ProductName = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const VariationCount = styled.span`
  font-size: 12px;
  color: #666;
  font-style: italic;
`

const StatusBadge = styled.span<{ $isActive: boolean }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${(props) => (props.$isActive ? '#d4edda' : '#f8d7da')};
  color: ${(props) => (props.$isActive ? '#155724' : '#721c24')};
  border: 1px solid ${(props) => (props.$isActive ? '#c3e6cb' : '#f5c6cb')};
`

const StoreChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`

const StoreChip = styled.span`
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  background-color: #e3f2fd;
  color: #1976d2;
  border: 1px solid #bbdefb;
  white-space: nowrap;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
`

export default ProductTable
